import React from 'react'
import { Box, Typography, styled } from '@mui/material'
import { Avatar, Button } from '@/design-system'
import { isValidAvatarUrl } from '@/utils/helper'
import { avatarGradient } from '../../utils'

const Card = styled(Box)({
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-lg)',
  boxShadow: 'var(--ds-shadow-soft)',
  padding: '22px 18px 18px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  transition: `transform var(--ds-duration-normal) var(--ds-ease), box-shadow var(--ds-duration-normal) var(--ds-ease)`,
  '&:hover': { transform: 'translateY(-3px)', boxShadow: 'var(--ds-shadow-lift)' },
})

const Name = styled(Typography)({ fontSize: 14.5, fontWeight: 680, letterSpacing: '-0.015em', color: 'var(--ds-ink)' })
const Prof = styled(Typography)({ fontSize: 11.5, color: 'var(--ds-ink-subtle)', marginTop: 2 })
const Reason = styled(Typography)({
  marginTop: 10,
  fontSize: 11,
  color: 'var(--ds-accent-deep-b, #8b5cf6)',
  background: 'color-mix(in srgb, var(--ds-accent-dot-3) 10%, transparent)',
  borderRadius: 'var(--ds-radius-pill)',
  padding: '4px 10px',
  lineHeight: 1.4,
})

export interface DiscoverCardProps {
  avatarUrl?: string
  name: string
  profession?: string
  location?: string
  reason: string
  connectState: 'idle' | 'sending' | 'sent'
  onConnect: () => void
  onClick?: () => void
}

export function DiscoverCard({
  avatarUrl,
  name,
  profession,
  location,
  reason,
  connectState,
  onConnect,
  onClick,
}: DiscoverCardProps) {
  const initial = name[0]?.toUpperCase() ?? '?'
  const metaLine = [profession, location].filter(Boolean).join(' · ')
  const resolvedSrc = avatarUrl && isValidAvatarUrl(avatarUrl) ? avatarUrl : undefined

  return (
    <Card>
      <Box onClick={onClick} sx={{ cursor: onClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar
          src={resolvedSrc}
          sx={{
            width: 60,
            height: 60,
            fontSize: 21,
            fontWeight: 700,
            marginBottom: '12px',
            background: avatarGradient(name),
            color: 'var(--ds-accent-ink)',
            boxShadow: '0 0 0 3px var(--ds-canvas), 0 0 0 4px var(--ds-border)',
          }}
        >
          {initial}
        </Avatar>
        <Name>{name}</Name>
        {metaLine && <Prof>{metaLine}</Prof>}
      </Box>
      <Reason>{reason}</Reason>
      <Button
        variant={connectState === 'sent' ? 'secondary' : 'primary'}
        size='sm'
        loading={connectState === 'sending'}
        disabled={connectState === 'sent'}
        onClick={onConnect}
        sx={{ marginTop: '14px', width: '100%' }}
      >
        {connectState === 'sent' ? 'Pending' : 'Connect'}
      </Button>
    </Card>
  )
}
